import React, { useEffect, useRef, useState } from 'react';
import CollapsibleCard from './CollapsibleCard';

// ── Leak & Usage Protection (paid tier) ───────────────────────────────────
//
// A collapsible box (via CollapsibleCard) holding all advanced alert settings
// in one place: slow-leak watch, rapid-leak/heavy-use rules (always ≥1, up to
// 5), and away mode. All write into the same claim-doc `alerts` block as the
// free alerts. When the device isn't on the paid tier, the real controls are
// shown greyed-out (a preview) behind a Subscribe banner — never a dead end.
//
//   alerts.leak  = { on }
//   alerts.usage = [{ id, on, gallons, windowMinutes }]   (first is permanent)
//   alerts.away  = { on, until }

const MAX_RULES = 5;
const FIRST_RULE_ID = 'rule1';               // stable id for the permanent first rule
const DEFAULT_RULE = { on: true, gallons: 100, windowMinutes: 60 };

const GALLON_PRESETS = [50, 100, 150, 200];
const WINDOW_OPTIONS = [
  { min: 30,   label: '30 min' },
  { min: 60,   label: '1 hr' },
  { min: 120,  label: '2 hr' },
  { min: 360,  label: '6 hr' },
  { min: 720,  label: '12 hr' },
  { min: 1440, label: '1 day' },
];

// Estimated irrigation run time — 30-minute increments up to 4 hours.
const IRRIGATION_OPTIONS = [30, 60, 90, 120, 150, 180, 210, 240].map((m) => {
  const h = Math.floor(m / 60), min = m % 60;
  const label = h && min ? `${h} hr ${min} min` : h ? `${h} hr` : `${min} min`;
  return { min: m, label };
});
const DEFAULT_IRRIGATION_MIN = 60;

// Quiet-hours time options — every 30 min, labelled in 12-hour clock. Value is
// the fractional hour (e.g. 1.5 = 1:30 AM), matching the detector's hour math.
const TIME_OPTIONS = [];
for (let m = 0; m < 24 * 60; m += 30) {
  const hr = Math.floor(m / 60), min = m % 60;
  const h12 = hr % 12 === 0 ? 12 : hr % 12;
  TIME_OPTIONS.push({ v: m / 60, label: `${h12}:${min === 0 ? '00' : '30'} ${hr < 12 ? 'AM' : 'PM'}` });
}
const DEFAULT_QUIET = { startHour: 1, endHour: 5 };

function newRuleId() {
  return 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// ── Icons ──────────────────────────────────────────────────────────────────
function IconShield() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l7 3v5c0 4.4-3 8.3-7 9.5C8 19.3 5 15.4 5 11V6l7-3z" />
      <path d="M9.2 12l2 2 3.6-4" />
    </svg>
  );
}
function IconLock() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export default function LeakUsageSection({ alerts, persist, entitled, previewForced, onEnable, dualTank, focus }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Deep-link targets. The leak modal sends people straight here from an alert
  // ("Change alert settings" / "Set irrigation time"), and this box is collapsed
  // by default, so landing on /alerts would otherwise show them a closed card
  // with no hint which control they came for.
  const awayRef = useRef(null);
  const irrigRef = useRef(null);
  const [enableErr, setEnableErr] = useState('');

  async function handleEnable() {
    if (!onEnable) return;
    setBusy(true);
    setEnableErr('');
    try {
      await onEnable();
    } catch (e) {
      setEnableErr("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const a = alerts || {};
  // Every family is opt-IN (default off), so each user picks what THEY receive.
  // Subscribing the device (the paid entitlement) unlocks these controls but
  // never turns anything on for anyone — receiving is always per-user.
  // Without Plus every switch SHOWS off, so a locked monitor never looks
  // protected. The saved choices in the claim doc are untouched (the locked
  // fieldset can't write), so turning Plus back on restores them as they were.
  // Writers below always build on the saved block `a`, never on these.
  const leakOn = entitled && a.leak?.on === true;
  const rapidOn = entitled && a.usageOn === true;    // master switch for the rule set (default off)
  const away = a.away || { on: false, until: '' };
  const awayOn = entitled && away.on === true;   // shown state (see leakOn)
  const rules = Array.isArray(a.usage) ? a.usage : [];

  // ── Writers (each merges into the whole alerts block, one save) ──
  const setLeak = (on) => persist({ ...a, leak: { ...(a.leak || {}), on } });
  // Custom quiet hours for the slow-leak watch (default 1–5am).
  const leakQuiet = (a.leak && a.leak.quiet) || DEFAULT_QUIET;
  const setLeakQuiet = (patch) => persist({ ...a, leak: { ...(a.leak || {}), quiet: { ...leakQuiet, ...patch } } });
  // Which tank the slow-leak watch covers. 1 or 2 scopes it; 0 means both, which
  // is what the cloud treats any non-1/2 value as (leakAppliesToTank). Only
  // offered on dual-tank units — a single-tank device has nothing to choose.
  const leakTank = (a.leak?.tank === 1 || a.leak?.tank === 2) ? a.leak.tank : 0;
  const setLeakTank = (tank) => persist({ ...a, leak: { ...(a.leak || {}), tank } });
  // Turning rapid ON is the opt-in: seed the permanent first rule then (stable
  // id, so nothing duplicates) if the user has none yet, in one write. Turning
  // OFF just clears the master switch and keeps any rules for next time. We
  // never write on mere entitlement / page view — only on a real user action.
  const setRapid = (on) => {
    if (on && rules.length === 0) {
      persist({ ...a, usageOn: true, usage: [{ id: FIRST_RULE_ID, ...DEFAULT_RULE }] });
    } else {
      persist({ ...a, usageOn: on });
    }
  };
  const setAway = (patch) => persist({ ...a, away: { ...away, ...patch } });
  // Monthly report. Accepts the legacy { on: true } shape as well as a bare
  // boolean, same as the server's gate does.
  const recapOn = entitled && (a.monthlyRecap === true || a.monthlyRecap?.on === true);
  const setRecap = (on) => persist({ ...a, monthlyRecap: on });
  // The report is emailed, so it needs the email channel on as well. Said on
  // the row rather than hidden, because switching it on and seeing nothing
  // arrive is the worse outcome.
  const emailOn = a.enabled === true;
  // Irrigation lives under away: { on, estMinutes }. Only meaningful while away.
  const irrigation = away.irrigation || { on: false, estMinutes: DEFAULT_IRRIGATION_MIN };
  const setIrrigation = (patch) => setAway({ irrigation: { ...irrigation, ...patch } });

  useEffect(() => {
    if (focus !== 'away' && focus !== 'irrigation') return undefined;
    setOpen(true);
    // The irrigation row is hidden while Away is off, so there is nothing to
    // scroll to — fall back to the Away toggle, which is what they need first.
    const wantIrrig = focus === 'irrigation' && awayOn;
    // After the card's open transition, or it scrolls to the collapsed height.
    const id = setTimeout(() => {
      const el = (wantIrrig ? irrigRef.current : awayRef.current);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => clearTimeout(id);
  }, [focus, awayOn]);
  const writeRules = (next) => persist({ ...a, usage: next });

  // What to render: the saved rules, or a single synthetic default as a preview
  // template before the user has opted in (nothing is written until they do).
  const displayRules = rules.length ? rules : [{ id: FIRST_RULE_ID, ...DEFAULT_RULE }];

  const addRule = () => {
    if (displayRules.length >= MAX_RULES) return;
    const base = rules.length ? rules : [{ id: FIRST_RULE_ID, ...DEFAULT_RULE }];
    writeRules([...base, { id: newRuleId(), ...DEFAULT_RULE }]);
  };
  const updateRule = (id, patch) => {
    // Materialize the synthetic first rule on first edit if it isn't saved yet.
    const base = rules.length ? rules : [{ id: FIRST_RULE_ID, ...DEFAULT_RULE }];
    writeRules(base.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeRule = (id) => writeRules(rules.filter((r) => r.id !== id));

  // Compact one-line state shown under the title while collapsed.
  let summary;
  if (!entitled) {
    summary = 'Catch leaks and heavy use, and get a monthly usage summary';
  } else {
    const parts = [];
    if (leakOn) parts.push('Leak watch on');
    const nRules = rapidOn ? displayRules.length : 0;
    if (nRules) parts.push(`${nRules} usage alert${nRules > 1 ? 's' : ''}`);
    if (awayOn) parts.push('Away mode on');
    if (recapOn) parts.push('Monthly summary');
    summary = parts.length ? parts.join(' · ') : 'Off — tap to set up';
  }

  // Only when locked: with Plus active the badge says nothing new (the
  // "Plus active" chip at the top of the page covers it).
  const badge = entitled ? null : (
    <span className="leakpro-pill"><IconLock /> SoKo Plus</span>
  );

  return (
    <CollapsibleCard
      icon={<IconShield />}
      title="Usage and leak alerts"
      badge={badge}
      summary={summary}
      open={open}
      onToggle={() => setOpen((v) => !v)}
      locked={!entitled}
    >
      {previewForced && (
        <div className="leakpro-dev">Preview mode — unlocked here for testing only.</div>
      )}

      {!entitled && (
        <div className="leakpro-lockbanner">
          <span>Usage and leak alerts are part of SoKo Plus.</span>
          <button type="button" className="btn btn-primary leakpro-lockbanner__btn"
            disabled={busy} onClick={handleEnable}>
            See SoKo Plus
          </button>
          {enableErr && <span className="leakpro-lockbanner__note">{enableErr}</span>}
        </div>
      )}

      {/* When not subscribed, the whole set is disabled + greyed as a preview. */}
      <fieldset className="leakpro-fields" disabled={!entitled}>
        {/* 1 — Watch for slow leaks */}
        <div className="alerts-thresholds" style={{ marginTop: 0, borderTop: 0, paddingTop: 0 }}>
          <label className="alerts-threshold-row" style={{ paddingTop: 4 }}>
            <input type="checkbox" checked={leakOn}
              onChange={(e) => setLeak(e.target.checked)} />
            <span>
              <span style={{ display: 'block', fontWeight: 500 }}>Watch for slow leaks</span>
              <span className="hint">
                We check your tank during quiet hours and notify you if it's
                slowly losing water on its own.
              </span>
            </span>
          </label>
          {/* Quiet hours — compact, only when the watch is on. Default 1–5am. */}
          {leakOn && (
            <div className="leakpro-away-until" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span className="hint">Quiet hours</span>
              <select className="leakpro-select" aria-label="Quiet hours start"
                value={String(leakQuiet.startHour)}
                onChange={(e) => setLeakQuiet({ startHour: parseFloat(e.target.value) })}>
                {TIME_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
              <span className="hint">to</span>
              <select className="leakpro-select" aria-label="Quiet hours end"
                value={String(leakQuiet.endHour)}
                onChange={(e) => setLeakQuiet({ endHour: parseFloat(e.target.value) })}>
                {TIME_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
          )}
          {/* Which tank to watch — dual-tank units only. A single-tank device
              has nothing to choose, so it sees no extra control at all. */}
          {leakOn && dualTank && (
            <div className="leakpro-away-until" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span className="hint">Watch</span>
              <select className="leakpro-select" aria-label="Which tank to watch for slow leaks"
                value={String(leakTank)}
                onChange={(e) => setLeakTank(parseInt(e.target.value, 10))}>
                <option value="0">Both tanks</option>
                <option value="1">Tank 1 only</option>
                <option value="2">Tank 2 only</option>
              </select>
            </div>
          )}
        </div>

        <div className="prefs-divider" style={{ margin: '14px -14px' }} />

        {/* 2 — Usage alerts. A single master toggle on the right
            (like the slow-leak and away rows). The rules sit below and simply
            grey out when the master is off — no per-rule switch. */}
        <div className="alerts-thresholds" style={{ marginTop: 0, borderTop: 0, paddingTop: 0 }}>
          <label className="alerts-threshold-row" style={{ paddingTop: 4 }}>
            <input type="checkbox" checked={rapidOn}
              onChange={(e) => setRapid(e.target.checked)} />
            <span>
              <span style={{ display: 'block', fontWeight: 500 }}>Usage alerts</span>
              <span className="hint">
                Get notified when a tank drops more than the amount you set. It
                could be a leak, a tap left running, or just more use than usual.
              </span>
            </span>
          </label>
        </div>

        <fieldset className="leakpro-rules-fields" disabled={!rapidOn}>
          <div className="alerts-section" style={{ marginTop: 6 }}>
            <div className="leakpro-rules">
              {displayRules.map((r, i) => (
                <RuleRow key={r.id} rule={r} canRemove={i > 0}
                  onGallons={(gallons) => updateRule(r.id, { gallons })}
                  onWindow={(windowMinutes) => updateRule(r.id, { windowMinutes })}
                  onRemove={() => removeRule(r.id)} />
              ))}
            </div>

            {displayRules.length < MAX_RULES ? (
              <button type="button" className="alerts-recipient-add"
                style={{ marginTop: 12 }} onClick={addRule}>
                + Add another alert
              </button>
            ) : (
              <div className="hint" style={{ marginTop: 12 }}>
                That's the most you can add (5).
              </div>
            )}
          </div>
        </fieldset>

        <div className="prefs-divider" style={{ margin: '14px -14px' }} />

        {/* 3 — Away mode */}
        <div ref={awayRef} className="alerts-thresholds alerts-deeplink-target"
          style={{ marginTop: 0, borderTop: 0, paddingTop: 0 }}>
          <label className="alerts-threshold-row" style={{ paddingTop: 4 }}>
            <input type="checkbox" checked={awayOn}
              onChange={(e) => setAway({ on: e.target.checked })} />
            <span>
              <span style={{ display: 'block', fontWeight: 500 }}>Away mode</span>
              <span className="hint">
                Get notified if your tank keeps losing water while you're away —
                we watch it around the clock.
              </span>
            </span>
          </label>
          {awayOn && (
            <div className="leakpro-away-until">
              <label htmlFor="away-until" className="hint" style={{ display: 'block', marginBottom: 6 }}>
                Turn off by itself on this day (optional):
              </label>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <input id="away-until" type="date" className="alerts-recipient-input"
                  style={{ maxWidth: 190 }} value={away.until || ''}
                  onChange={(e) => setAway({ until: e.target.value })} />
                {away.until && (
                  <button type="button" className="leakpro-rule__remove"
                    onClick={() => setAway({ until: '' })}
                    aria-label="Clear the turn-off date">×</button>
                )}
              </span>
            </div>
          )}

          {/* Irrigation — only meaningful while away, so the whole control is
              hidden (not just greyed) when Away is off. The saved setting is
              kept either way: setAway spreads the existing `away` object, so
              irrigation.on / estMinutes survive toggling Away off and come
              back as they were. When on, a leak must keep dropping LONGER than
              the estimated run time, so a scheduled watering cycle isn't
              mistaken for a leak. The control and its run-time picker are
              indented under Away; the explanatory line below stays at the base
              indentation. */}
          {awayOn && (
            <>
              <label className="alerts-threshold-row" style={{ paddingTop: 10, paddingLeft: 24 }}>
                <input ref={irrigRef} type="checkbox" checked={irrigation.on === true}
                  onChange={(e) => setIrrigation({ on: e.target.checked })} />
                <span>
                  <span style={{ display: 'block', fontWeight: 500 }}>Ignore irrigation usage:</span>
                  <span className="hint">So scheduled watering isn't flagged as a leak.</span>
                </span>
              </label>
              {irrigation.on === true && (
                <>
                  <div className="leakpro-away-until" style={{ paddingLeft: 24 }}>
                    <label htmlFor="irrig-mins" className="hint" style={{ display: 'block', marginBottom: 6 }}>
                      Estimated run time:
                    </label>
                    <select id="irrig-mins" className="leakpro-select" style={{ maxWidth: 190 }}
                      value={String(irrigation.estMinutes || DEFAULT_IRRIGATION_MIN)}
                      onChange={(e) => setIrrigation({ estMinutes: parseInt(e.target.value, 10) })}>
                      {IRRIGATION_OPTIONS.map((o) => <option key={o.min} value={o.min}>{o.label}</option>)}
                    </select>
                  </div>
                  <span className="hint" style={{ display: 'block', marginTop: 6 }}>
                    You'll only receive alerts if your water level drops longer than your
                    irrigation run time. Use a slightly longer time to avoid false alerts.
                  </span>
                </>
              )}
            </>
          )}
        </div>

        <div className="prefs-divider" style={{ margin: '14px -14px' }} />

        {/* 4 — Monthly report.
            Lives here, not under Notification methods: that card answers "how
            do we reach you" — email, push, text — and this is a thing we tell
            you about, not a channel. Here it also sits with everything else
            the tier unlocks, which is what someone who just paid wants to see
            in one place. Defaulted on by the server at activation, so this is
            a switch to turn OFF. */}
        <div className="alerts-thresholds" style={{ marginTop: 0, borderTop: 0, paddingTop: 0 }}>
          <label className="alerts-threshold-row" style={{ paddingTop: 4 }}>
            <input type="checkbox" checked={recapOn}
              onChange={(e) => setRecap(e.target.checked)} />
            <span>
              <span style={{ display: 'block', fontWeight: 500 }}>Monthly usage summary</span>
              <span className="hint">
                {emailOn
                  ? 'An email with last month’s water use, sent on the 1st.'
                  : 'Turn on email alerts under Notification methods to receive this.'}
              </span>
            </span>
          </label>
        </div>
      </fieldset>
    </CollapsibleCard>
  );
}

// ── One rapid-use rule: "Tell me if tank level drops [gal] within [time]." ───
function RuleRow({ rule, canRemove, onGallons, onWindow, onRemove }) {
  const isPreset = GALLON_PRESETS.includes(rule.gallons);
  const [customOpen, setCustomOpen] = useState(!isPreset);

  return (
    <div className="leakpro-rule">
      <div className="leakpro-rule__body">
        <span className="leakpro-rule__label">Tell me if tank level drops</span>
        <div className="leakpro-rule__controls">
          <select className="leakpro-select"
            value={customOpen ? 'custom' : String(rule.gallons)}
            onChange={(e) => {
              if (e.target.value === 'custom') { setCustomOpen(true); return; }
              setCustomOpen(false);
              onGallons(parseInt(e.target.value, 10));
            }}>
            {GALLON_PRESETS.map((g) => <option key={g} value={g}>{g} gal</option>)}
            <option value="custom">Custom…</option>
          </select>
          {customOpen && (
            <input type="text" inputMode="numeric" className="leakpro-custom-gal"
              value={rule.gallons} maxLength={4}
              onChange={(e) => {
                const raw = e.target.value.replace(/\D/g, '');
                onGallons(raw === '' ? 0 : parseInt(raw, 10));
              }}
              aria-label="Custom gallons" />
          )}
          {customOpen && <span className="leakpro-unit">gal</span>}
          <span className="leakpro-within">within</span>
          <select className="leakpro-select" value={String(rule.windowMinutes)}
            onChange={(e) => onWindow(parseInt(e.target.value, 10))}>
            {WINDOW_OPTIONS.map((w) => <option key={w.min} value={w.min}>{w.label}</option>)}
          </select>
        </div>
      </div>

      {canRemove ? (
        <button type="button" className="leakpro-rule__remove" onClick={onRemove}
          aria-label="Remove this alert">×</button>
      ) : (
        <span className="leakpro-rule__remove-spacer" aria-hidden="true" />
      )}
    </div>
  );
}
